import React, { useState, useRef, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Upload, Download, FileSpreadsheet, CheckCircle, XCircle, AlertTriangle, Loader2, RefreshCw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';

interface ImportarClientesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

interface ClienteRow {
  nombre: string;
  direccion?: string;
  telefono?: string;
  email?: string;
  nif_cif?: string;
  nombre_fiscal?: string;
  direccion_fiscal?: string;
  poblacion?: string;
  codigo_postal?: string;
  telefono_facturacion?: string;
  email_facturacion?: string;
  numero_abonado?: string;
  palabra_clave_cra?: string;
  palabra_coaccion?: string;
  contrasena_cra?: string;
  contacto_1_nombre?: string;
  contacto_1_telefono?: string;
  contacto_1_cargo?: string;
  contacto_2_nombre?: string;
  contacto_2_telefono?: string;
  contacto_2_cargo?: string;
  contacto_3_nombre?: string;
  contacto_3_telefono?: string;
  contacto_3_cargo?: string;
}

interface ParsedRow {
  data: ClienteRow;
  errors: string[];
  rowIndex: number;
  existingId?: string; // ID of existing client if duplicate
}

type ImportStep = 'upload' | 'preview' | 'importing' | 'done';

const TEMPLATE_COLUMNS = [
  'nombre', 'direccion', 'telefono', 'email', 'nif_cif',
  'nombre_fiscal', 'direccion_fiscal', 'poblacion',
  'codigo_postal', 'telefono_facturacion', 'email_facturacion',
  'numero_abonado', 'palabra_clave_cra', 'palabra_coaccion', 'contrasena_cra',
  'contacto_1_nombre', 'contacto_1_telefono', 'contacto_1_cargo',
  'contacto_2_nombre', 'contacto_2_telefono', 'contacto_2_cargo',
  'contacto_3_nombre', 'contacto_3_telefono', 'contacto_3_cargo',
];

const COLUMN_LABELS: Record<string, string> = {
  nombre: 'Nombre *',
  direccion: 'Dirección Instalación',
  telefono: 'Teléfono',
  email: 'Email',
  nif_cif: 'NIF/CIF',
  nombre_fiscal: 'Nombre Fiscal',
  direccion_fiscal: 'Dirección Facturación',
  poblacion: 'Población',
  codigo_postal: 'Código Postal',
  telefono_facturacion: 'Teléfono Facturación',
  email_facturacion: 'Email Facturación',
  numero_abonado: 'Nº Abonado',
  palabra_clave_cra: 'Palabra Clave CRA',
  palabra_coaccion: 'Palabra Coacción',
  contrasena_cra: 'Contraseña CRA',
  contacto_1_nombre: 'Contacto 1 Nombre',
  contacto_1_telefono: 'Contacto 1 Teléfono',
  contacto_1_cargo: 'Contacto 1 Cargo',
  contacto_2_nombre: 'Contacto 2 Nombre',
  contacto_2_telefono: 'Contacto 2 Teléfono',
  contacto_2_cargo: 'Contacto 2 Cargo',
  contacto_3_nombre: 'Contacto 3 Nombre',
  contacto_3_telefono: 'Contacto 3 Teléfono',
  contacto_3_cargo: 'Contacto 3 Cargo',
};

const BATCH_SIZE = 50;

function buildContactos(data: ClienteRow) {
  const contactos = [];
  for (let i = 1; i <= 3; i++) {
    const nombre = data[`contacto_${i}_nombre` as keyof ClienteRow];
    const telefono = data[`contacto_${i}_telefono` as keyof ClienteRow];
    const cargo = data[`contacto_${i}_cargo` as keyof ClienteRow];
    if (nombre) {
      contactos.push({ nombre, telefono: telefono || '', cargo: cargo || '' });
    }
  }
  return contactos.length > 0 ? contactos : null;
}

function buildClientePayload(data: ClienteRow, empresaId: string) {
  return {
    nombre: data.nombre,
    direccion: data.direccion || null,
    telefono: data.telefono || null,
    email: data.email || null,
    nif_cif: data.nif_cif || null,
    nombre_fiscal: data.nombre_fiscal || null,
    direccion_fiscal: data.direccion_fiscal || null,
    poblacion: data.poblacion || null,
    codigo_postal: data.codigo_postal || null,
    telefono_facturacion: data.telefono_facturacion || null,
    email_facturacion: data.email_facturacion || null,
    numero_abonado: data.numero_abonado || null,
    palabra_clave_cra: data.palabra_clave_cra || null,
    palabra_coaccion: data.palabra_coaccion || null,
    contrasena_cra: data.contrasena_cra || null,
    personas_contacto: buildContactos(data),
    empresa_id: empresaId,
  };
}

export const ImportarClientesDialog: React.FC<ImportarClientesDialogProps> = ({
  open,
  onOpenChange,
  onSuccess,
}) => {
  const { perfil } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<ImportStep>('upload');
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [progress, setProgress] = useState(0);
  const [importResult, setImportResult] = useState<{ created: number; updated: number; errors: number }>({ created: 0, updated: 0, errors: 0 });

  const reset = useCallback(() => {
    setStep('upload');
    setParsedRows([]);
    setProgress(0);
    setImportResult({ created: 0, updated: 0, errors: 0 });
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleOpenChange = (value: boolean) => {
    if (!value) reset();
    onOpenChange(value);
  };

  const downloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      TEMPLATE_COLUMNS.map(c => COLUMN_LABELS[c] || c),
      ['Bar El Rincón', 'Calle Mayor 5', '612345678', 'info@bar.com', 'B12345678',
       'El Rincón SL', 'Calle Mayor 5', 'Madrid', '28001', '912345678', 'facturacion@bar.com',
       'AB-99001', 'ALARMA123', 'SOCORRO', '1234',
       'Juan López', '612345678', 'Gerente',
       'María García', '698765432', 'Encargada',
       '', '', ''],
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Clientes');
    ws['!cols'] = TEMPLATE_COLUMNS.map(() => ({ wch: 18 }));
    XLSX.writeFile(wb, 'plantilla_clientes.xlsx');
  };

  const parseFile = async (file: File) => {
    if (!perfil?.empresa_id) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json<Record<string, any>>(sheet, { defval: '' });

        if (jsonData.length === 0) {
          toast({ title: 'Archivo vacío', description: 'El archivo no contiene datos.', variant: 'destructive' });
          return;
        }

        // Load existing clients for duplicate detection
        const { data: existingClients } = await supabase
          .from('clientes')
          .select('id, nombre, nif_cif')
          .eq('empresa_id', perfil.empresa_id);

        const existingByNif = new Map<string, string>();
        const existingByName = new Map<string, string>();
        (existingClients || []).forEach(c => {
          if (c.nif_cif) existingByNif.set(c.nif_cif.toLowerCase().trim(), c.id);
          existingByName.set(c.nombre.toLowerCase().trim(), c.id);
        });

        // Build reverse map for header matching
        const reverseMap: Record<string, string> = {};
        for (const key of TEMPLATE_COLUMNS) {
          reverseMap[(COLUMN_LABELS[key] || key).toLowerCase().replace(/\s*\*\s*/g, '').trim()] = key;
          reverseMap[key.toLowerCase()] = key;
        }

        const rows: ParsedRow[] = jsonData.map((row, idx) => {
          const mapped: Record<string, string> = {};
          for (const [header, value] of Object.entries(row)) {
            const normalizedHeader = header.toLowerCase().replace(/\s*\*\s*/g, '').trim();
            const matchedKey = reverseMap[normalizedHeader];
            if (matchedKey) {
              mapped[matchedKey] = String(value || '').trim();
            }
          }

          const errors: string[] = [];
          if (!mapped.nombre) errors.push('Falta nombre');

          // Duplicate detection
          let existingId: string | undefined;
          if (mapped.nif_cif) {
            existingId = existingByNif.get(mapped.nif_cif.toLowerCase().trim());
          }
          if (!existingId && mapped.nombre) {
            existingId = existingByName.get(mapped.nombre.toLowerCase().trim());
          }

          return {
            data: mapped as unknown as ClienteRow,
            errors,
            rowIndex: idx + 2,
            existingId,
          };
        });

        setParsedRows(rows);
        setStep('preview');
      } catch {
        toast({ title: 'Error al leer archivo', description: 'No se pudo parsear el archivo. Verifica el formato.', variant: 'destructive' });
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) parseFile(file);
  };

  const validRows = parsedRows.filter(r => r.errors.length === 0);
  const invalidRows = parsedRows.filter(r => r.errors.length > 0);
  const newRows = validRows.filter(r => !r.existingId);
  const updateRows = validRows.filter(r => !!r.existingId);

  const handleImport = async () => {
    if (!perfil?.empresa_id) {
      toast({ title: 'Error', description: 'No se pudo determinar la empresa del usuario.', variant: 'destructive' });
      return;
    }

    setStep('importing');
    let created = 0;
    let updated = 0;
    let errors = 0;
    const totalOps = validRows.length;

    // Insert new clients in batches
    const inserts = newRows.map(r => buildClientePayload(r.data, perfil.empresa_id!));
    for (let i = 0; i < inserts.length; i += BATCH_SIZE) {
      const batch = inserts.slice(i, i + BATCH_SIZE);
      const { error } = await supabase.from('clientes').insert(batch as any);
      if (error) {
        console.error('Batch insert error:', error);
        errors += batch.length;
      } else {
        created += batch.length;
      }
      setProgress(Math.round(((created + updated + errors) / totalOps) * 100));
    }

    // Update existing clients one by one (each has different ID)
    for (const row of updateRows) {
      const payload = buildClientePayload(row.data, perfil.empresa_id!);
      const { empresa_id: _, ...updateData } = payload;
      const { error } = await supabase.from('clientes').update(updateData as any).eq('id', row.existingId!);
      if (error) {
        console.error('Update error:', error);
        errors++;
      } else {
        updated++;
      }
      setProgress(Math.round(((created + updated + errors) / totalOps) * 100));
    }

    setImportResult({ created, updated, errors: errors + invalidRows.length });
    setStep('done');
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[750px] max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            Importar Clientes
          </DialogTitle>
          <DialogDescription>
            Sube un archivo Excel o CSV. Los clientes existentes se actualizarán automáticamente.
          </DialogDescription>
        </DialogHeader>

        {step === 'upload' && (
          <div className="space-y-6 py-4">
            <div className="flex justify-center">
              <Button variant="outline" onClick={downloadTemplate} className="gap-2">
                <Download className="h-4 w-4" />
                Descargar plantilla Excel
              </Button>
            </div>

            <div
              className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 hover:bg-muted/50 transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
              <p className="font-medium">Haz clic para seleccionar un archivo</p>
              <p className="text-sm text-muted-foreground mt-1">
                Formatos aceptados: .xlsx, .xls, .csv
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>
          </div>
        )}

        {step === 'preview' && (
          <div className="space-y-4 flex-1 min-h-0">
            <div className="flex items-center gap-3 flex-wrap">
              {newRows.length > 0 && (
                <Badge variant="default" className="gap-1">
                  <CheckCircle className="h-3 w-3" />
                  {newRows.length} nuevos
                </Badge>
              )}
              {updateRows.length > 0 && (
                <Badge className="gap-1 bg-blue-600">
                  <RefreshCw className="h-3 w-3" />
                  {updateRows.length} a actualizar
                </Badge>
              )}
              {invalidRows.length > 0 && (
                <Badge variant="destructive" className="gap-1">
                  <XCircle className="h-3 w-3" />
                  {invalidRows.length} con errores
                </Badge>
              )}
              <span className="text-sm text-muted-foreground">
                Total: {parsedRows.length} filas
              </span>
            </div>

            <ScrollArea className="h-[350px] border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>Nombre</TableHead>
                    <TableHead>NIF/CIF</TableHead>
                    <TableHead>Teléfono</TableHead>
                    <TableHead>Nº Abonado</TableHead>
                    <TableHead className="w-28">Estado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {parsedRows.map((row) => (
                    <TableRow
                      key={row.rowIndex}
                      className={row.errors.length > 0 ? 'bg-destructive/10' : ''}
                    >
                      <TableCell className="text-muted-foreground text-xs">{row.rowIndex}</TableCell>
                      <TableCell className="font-medium">{row.data.nombre || '—'}</TableCell>
                      <TableCell className="text-sm">{row.data.nif_cif || '—'}</TableCell>
                      <TableCell className="text-sm">{row.data.telefono || '—'}</TableCell>
                      <TableCell className="text-sm">{row.data.numero_abonado || '—'}</TableCell>
                      <TableCell>
                        {row.errors.length > 0 ? (
                          <span className="text-destructive text-xs flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" />
                            {row.errors[0]}
                          </span>
                        ) : row.existingId ? (
                          <Badge variant="outline" className="text-xs gap-1 border-blue-500 text-blue-600">
                            <RefreshCw className="h-3 w-3" />
                            Actualizar
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs gap-1 border-green-500 text-green-600">
                            <CheckCircle className="h-3 w-3" />
                            Nuevo
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>

            <div className="flex justify-between items-center pt-2">
              <Button variant="outline" onClick={reset}>
                Cancelar
              </Button>
              <Button
                onClick={handleImport}
                disabled={validRows.length === 0}
                className="gap-2"
              >
                <Upload className="h-4 w-4" />
                Importar {validRows.length} clientes
              </Button>
            </div>
          </div>
        )}

        {step === 'importing' && (
          <div className="py-8 space-y-4">
            <div className="flex items-center justify-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="font-medium">Importando clientes...</span>
            </div>
            <Progress value={progress} className="h-2" />
            <p className="text-center text-sm text-muted-foreground">{progress}%</p>
          </div>
        )}

        {step === 'done' && (
          <div className="py-8 space-y-4 text-center">
            <CheckCircle className="h-12 w-12 mx-auto text-green-500" />
            <h3 className="text-lg font-semibold">Importación completada</h3>
            <div className="flex justify-center gap-4 flex-wrap">
              {importResult.created > 0 && (
                <Badge variant="default" className="text-sm gap-1">
                  <CheckCircle className="h-3 w-3" />
                  {importResult.created} creados
                </Badge>
              )}
              {importResult.updated > 0 && (
                <Badge className="text-sm gap-1 bg-blue-600">
                  <RefreshCw className="h-3 w-3" />
                  {importResult.updated} actualizados
                </Badge>
              )}
              {importResult.errors > 0 && (
                <Badge variant="destructive" className="text-sm gap-1">
                  <XCircle className="h-3 w-3" />
                  {importResult.errors} con errores
                </Badge>
              )}
            </div>
            <Button
              onClick={() => {
                handleOpenChange(false);
                onSuccess();
              }}
              className="mt-4"
            >
              Cerrar
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
